import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';

type Row = {
  organization_id: string; contract_id?: string; client_id?: string; contract_name?: string; client_name?: string;
  threshold: number; revenue: number; labour: number; parts: number; subcontract: number; travel: number; other: number;
  total_cost: number; margin: number; margin_pct: number | null; below_threshold: boolean;
};

export function Profitability() {
  const { t, i18n } = useTranslation();
  const { currency } = useOrg();
  const loc = i18n.language;
  const [tab, setTab] = useState<'contract' | 'client'>('contract');
  const [contracts, setContracts] = useState<Row[]>([]);
  const [clients, setClients] = useState<Row[]>([]);
  const money = (n: number) => formatCurrency(n, currency, loc);

  const load = useCallback(async () => {
    const [c, cl] = await Promise.all([
      supabase.from('v_profitability_contract').select('*'),
      supabase.from('v_profitability_client').select('*'),
    ]);
    setContracts((c.data as any) ?? []);
    setClients((cl.data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const rows = tab === 'contract' ? contracts : clients;
  const threshold = rows[0]?.threshold ?? 20;
  const nameOf = (r: Row) => (tab === 'contract' ? r.contract_name : r.client_name) ?? '—';

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 py-6">
        <div className="mb-5 flex items-end justify-between">
          <div>
            <h1 className="text-xl font-semibold">{t('nav.profitability')}</h1>
            <p className="text-sm text-muted">{t('prof.subtitle')}</p>
          </div>
          <div className="rounded-pill bg-bg px-3 py-1 text-sm text-muted">{t('prof.threshold')}: {threshold}%</div>
        </div>

        <div className="mb-5 flex gap-1 border-b border-border">
          {(['contract', 'client'] as const).map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} className={cn('px-4 py-2 text-sm', tab === tb ? 'border-b-2 border-accent-dark font-medium text-fg' : 'text-muted')}>{t('prof.by_' + tb)}</button>
          ))}
        </div>

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-4 py-2">{t('prof.' + tab)}</th>
                <th className="px-4 py-2 text-right">{t('pnl.revenue')}</th>
                <th className="px-4 py-2 text-right">{t('cost.labour')}</th>
                <th className="px-4 py-2 text-right">{t('cost.part')}</th>
                <th className="px-4 py-2 text-right">{t('cost.subcontract')}</th>
                <th className="px-4 py-2 text-right">{t('cost.travel')}</th>
                <th className="px-4 py-2 text-right">{t('prof.cost')}</th>
                <th className="px-4 py-2 text-right">{t('pnl.margin')}</th>
                <th className="px-4 py-2 text-right">{t('prof.marginPct')}</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.contract_id ?? r.client_id} className="border-t border-divider">
                    <td className="px-4 py-2">{nameOf(r)}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(Number(r.revenue))}</td>
                    <td className="px-4 py-2 text-right font-mono text-muted">{money(Number(r.labour))}</td>
                    <td className="px-4 py-2 text-right font-mono text-muted">{money(Number(r.parts))}</td>
                    <td className="px-4 py-2 text-right font-mono text-muted">{money(Number(r.subcontract))}</td>
                    <td className="px-4 py-2 text-right font-mono text-muted">{money(Number(r.travel))}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(Number(r.total_cost))}</td>
                    <td className={cn('px-4 py-2 text-right font-mono', Number(r.margin) < 0 ? 'text-st-overdue-fg' : '')}>{money(Number(r.margin))}</td>
                    <td className="px-4 py-2 text-right">
                      {r.margin_pct == null ? <span className="text-muted">—</span> : (
                        r.below_threshold
                          ? <StatusPill tone="overdue">{r.margin_pct}%</StatusPill>
                          : <span className="font-mono">{r.margin_pct}%</span>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted">{t('common.noData')}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
        <p className="mt-3 text-xs text-muted">{t('prof.belowNote')}</p>
      </div>
    </Layout>
  );
}
